/**
 * Omniture Event Queue with Retry
 *
 * Singleton queue that buffers tracking events until pwatracker.js loads
 * and `window.s` becomes available. Retries up to QUEUE_MAX_RETRIES times
 * at QUEUE_RETRY_INTERVAL_MS intervals.
 *
 * Port of PAY-GO-MMT omniture-v1.js OmnitureEventQueue, rewritten in TypeScript.
 */
import type { OmnitureData, OmnitureEventType, OmnitureQueueItem } from './types';
import { QUEUE_MAX_RETRIES, QUEUE_RETRY_INTERVAL_MS } from './constants';

class OmnitureEventQueue {
  private eventQueue: OmnitureQueueItem[] = [];
  private isRetrying = false;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private hasExhaustedRetries = false;

  private isOmnitureAvailable(): boolean {
    return typeof window.s !== 'undefined';
  }

  private executeOmnitureCall(item: OmnitureQueueItem): void {
    const omni = window.s;
    if (!omni) return;

    try {
      Object.entries(item.omnitureData).forEach(([key, value]) => {
        omni[key] = value;
      });

      omni.linkTrackVars = Object.keys(item.omnitureData).join(',');

      if (item.eventType === 'CLICK') {
        const linkName = String(item.omnitureData['prop54'] ?? 'link_click');
        omni.tl(omni, 'o', linkName);
        omni.clearVars();
      } else {
        omni.t();
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Omniture call failed:', error.message);
      }
    }
  }

  private processEventQueue(): void {
    while (this.eventQueue.length > 0) {
      const item = this.eventQueue.shift();
      if (item) {
        this.executeOmnitureCall(item);
      }
    }
  }

  private resetRetryState(): void {
    this.isRetrying = false;
    this.retryCount = 0;
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private attemptExecution(): void {
    if (this.isOmnitureAvailable()) {
      this.clearRetryTimer();
      this.resetRetryState();
      this.hasExhaustedRetries = false;
      this.processEventQueue();
      return;
    }

    this.retryCount++;

    if (this.retryCount >= QUEUE_MAX_RETRIES) {
      this.clearRetryTimer();
      this.resetRetryState();
      this.hasExhaustedRetries = true;
      return;
    }

    this.retryTimer = setTimeout(() => {
      this.attemptExecution();
    }, QUEUE_RETRY_INTERVAL_MS);
  }

  private startRetryMechanism(): void {
    if (this.isRetrying) return;
    this.isRetrying = true;
    this.retryCount = 0;
    this.attemptExecution();
  }

  enqueueEvent(omnitureData: OmnitureData, eventType: OmnitureEventType): void {
    this.eventQueue.push({ omnitureData, eventType });

    if (this.isOmnitureAvailable()) {
      this.hasExhaustedRetries = false;
      this.processEventQueue();
      return;
    }

    if (this.isRetrying) return;

    if (this.hasExhaustedRetries) {
      this.hasExhaustedRetries = false;
      this.retryCount = QUEUE_MAX_RETRIES - 1;
    }

    this.startRetryMechanism();
  }
}

export const omnitureQueue = new OmnitureEventQueue();
